import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { Rechenabbruch, api, kurswert, periodenname } from './api'
import { useHintergrundabruf } from './Abruf'
import { fachlicherSatz } from './Fehlersatz'
import { zeigtSelbst } from './Stoerungsstelle'
import { alsEingabe, anwendername, anzahlwort, liesDezimal, ohneRegelvermerk, satz, t, zeitpunkt } from './sprache'
import type { Sprachschluessel } from './sprache'
import { Dateiwahl } from './Dateiwahl'
import { Dialogfenster } from './Dialogfenster'
import { sortiere } from './Sortierung'
import type { Sortierwahl } from './Sortierung'
import { Auswahlfeld, ausStammdatum } from './Auswahlfeld'
import { Periodenfeld } from './Periodenfeld'
import { useDarf } from './Rechte'
import { Filterkopfzeile, anzahlFilter, gefiltert } from './Tabellenfilter'
import type { Filterstand, Filterzelle, Spaltenbeschreibung } from './Tabellenfilter'
import { Spaltenmassleiste, useSpaltenmasse } from './Spaltenbreiten'
import { useGemerkt } from './Gemerkt'
import type {
  Kursart,
  Listeneintrag,
  Kurstafel,
  Kurstafelzeile,
  Kurszelle,
  Kursvorschau,
  Waehrungspaar,
  Waehrungspaartafel,
} from './typen'

/**
 * Der Arbeitsbereich Wechselkurse (7.6).
 *
 * **Eine Tabelle mit Zeitachse und kein Blatt aus Eigenschaften.** 7.4 hat die
 * Kurspflege ausdrücklich ausgenommen (Abschnitt 9, Punkt 2) und ihr eine
 * eigene Maske versprochen; hier ist sie. Der Grund für die Ausnahme ist die
 * Gestalt: Ein Konto hat Felder, eine Kurstabelle hat Zeilen — hundert
 * Perioden mal drei Kursarten in ein Eigenschaftsblatt zu legen, hieße
 * dreihundert Felder untereinander.
 *
 * **Dreispaltig wie die Stammdatenpflege und der Anhang**, aus demselben
 * Grund: links wählen, in der Mitte arbeiten, rechts nachschlagen. Links
 * stehen die Währungstabellen, in der Mitte die Perioden mit ihren Kursen,
 * rechts die Herkunft der angeklickten Zelle.
 *
 * **Drei Quellen, eine Vorschau** (R647–R649): Datei, EZB und die
 * YTD-Rechnung laufen alle durch denselben Bildschirm — sehen, was daraus
 * würde, dann übernehmen. Drei getrennte Wege hätten dreimal dieselbe Frage
 * beantwortet, und der Anwender müsste dreimal dasselbe lernen.
 */

/** Die Kursarten in der Reihenfolge, in der sie in der Tabelle stehen. */
const KURSARTEN: { art: Kursart; feld: 'stichtag' | 'durchschnitt' | 'ytd'; kopf: Sprachschluessel }[] = [
  { art: 'Stichtag', feld: 'stichtag', kopf: 'kurse.spalte.stichtag' },
  { art: 'Durchschnitt', feld: 'durchschnitt', kopf: 'kurse.spalte.durchschnitt' },
  { art: 'Ytd', feld: 'ytd', kopf: 'kurse.spalte.ytd' },
]

const HERKUNFT: Record<string, Sprachschluessel> = {
  importiert: 'kurse.herkunft.importiert',
  manuell: 'kurse.herkunft.manuell',
  fortgeschrieben: 'kurse.herkunft.fortgeschrieben',
  gerechnet: 'kurse.herkunft.gerechnet',
}

const BEFUND: Record<string, Sprachschluessel> = {
  neu: 'kurse.befund.neu',
  geaendert: 'kurse.befund.geaendert',
  unveraendert: 'kurse.befund.unveraendert',
  abgewiesen: 'kurse.befund.abgewiesen',
}

/** Ersatz, wenn die Währungsauswahl im Hintergrund nicht kam (R1212): keine Wahl. */
const KEINE_WAEHRUNGEN: Listeneintrag[] = []

export function Wechselkurse() {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('wechselkurse:wechselkurse')
  // Was der Angemeldete nicht darf, steht nicht da — kein Knopf, kein offenes Feld (R1180).
  const darf = useDarf()

  const [tafel, setTafel] = useState<Waehrungspaartafel | null>(null)
  const [paar, setPaar] = useState<{ von: string; nach: string } | null>(null)
  const [kurse, setKurse] = useState<Kurstafel | null>(null)
  const [suche, setSuche] = useState('')
  const [wahl, setWahl] = useState<Sortierwahl>(null)
  const [filter, setFilter] = useState<Filterstand>({})
  const [zelle, setZelle] = useState<{ periode: number; art: Kursart } | null>(null)
  const [vorschau, setVorschau] = useState<Kursvorschau | null>(null)
  // **Der fertige Satz, nur Fachliches** (7.2 Abschnitt 30, R1004): Eine
  // Störung steht im Störungsfenster. Bis zum 2026-10-04 stand hier für jeden
  // Fehler „Das ließ sich nicht schreiben." — auch wenn die EZB schwieg.
  const [meldung, setMeldung] = useState<string | null>(null)
  // Seit wann der EZB-Abruf wartet (R1008) — `null`, solange keiner läuft.
  const [ezbSeit, setEzbSeit] = useState<number | null>(null)
  const [befund, setBefund] = useState<Rechenabbruch | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  // Der Ausschnitt der Kurstabelle bleibt stehen (R1363).
  const [von, setVon] = useGemerkt('wechselkurse.von', null)
  const [bis, setBis] = useGemerkt('wechselkurse.bis', null)

  /*
    **Der Sammelabruf trägt seinen eigenen Zeitraum** (R652). Die Felder der
    Leiste beantworten „Welchen Ausschnitt dieser einen Tabelle sehe ich?", die
    Felder oben „Welchen Zeitraum hole ich für alle?" — ein Feld für beides
    beantwortet die zweite Frage heimlich mit der Antwort auf die erste.
  */
  const [sammelVon, setSammelVon] = useState<number | null>(null)
  const [sammelBis, setSammelBis] = useState<number | null>(null)

  /*
    **Eine Tabelle anlegen** (R683, seit 2026-09-21). Bis dahin ergab sich eine
    Tabelle nur aus dem Bestand an Gesellschaften und Kreisen — und war damit
    vor der ersten Gesellschaft nicht herstellbar. Wer im Dezember die
    Januarkurse einer Tochter pflegen will, die im Februar angelegt wird, tut
    genau das, wofür das Anlegen da ist.
  */
  const [anlegen, setAnlegen] = useState(false)
  const [neuVon, setNeuVon] = useState('')
  const [neuNach, setNeuNach] = useState('')
  // Eine Auswahlliste — Abruf im Hintergrund, Ersatz: keine Wahl (R1211).
  const waehrungen = useHintergrundabruf(
    anlegen ? () => api.stammdatenliste('waehrung').then((liste) => liste.eintraege) : null,
    [anlegen],
    KEINE_WAEHRUNGEN,
  )

  async function legeTabelleAn() {
    setLaeuft(true)
    setBefund(null)
    try {
      setTafel(await api.kurstabelleAnlegen(neuVon, neuNach))
      setPaar({ von: neuVon, nach: neuNach })
      setAnlegen(false)
      setNeuVon('')
      setNeuNach('')
    } catch (fehler) {
      // Nur ein Befund ist ein Befund — eine Störung steht im Fenster (R1003).
      if (fehler instanceof Rechenabbruch) {
        setBefund(fehler)
      } else {
        setMeldung(fachlicherSatz(fehler))
      }
    } finally {
      setLaeuft(false)
    }
  }

  useEffect(() => {
    api
      .waehrungspaare()
      .then((t) => {
        setTafel(t)

        // **Eine Vorgabe, die man sieht, ist keine Annahme** (R652): Das
        // laufende Geschäftsjahr steht im Feld, bevor der Anwender den Befehl
        // auslöst — er sieht, was er holt, und kann es ändern.
        setSammelVon(t.geschaeftsjahr.von)
        setSammelBis(t.geschaeftsjahr.bis)

        const erstes = t.paare.find((p) => p.gepflegt) ?? t.paare[0]
        if (erstes) {
          setPaar({ von: erstes.von, nach: erstes.nach })
        }
      })
      .catch((f) => setMeldung(fachlicherSatz(f)))
  }, [])

  useEffect(() => {
    if (!paar) {
      setKurse(null)
      return
    }

    let gilt = true
    setKurse(null)
    setZelle(null)
    // Die Jahresklammer faengt bei jedem Paar mit ihrer Vorgabe an (R653).
    setOffen(null)

    api
      .kurstafel(
        paar.von,
        paar.nach,
        von ?? undefined,
        bis ?? undefined,
      )
      .then((k) => gilt && setKurse(k))
      .catch((f) => gilt && setMeldung(fachlicherSatz(f)))

    return () => {
      gilt = false
    }
  }, [paar?.von, paar?.nach, von, bis])

  const spalten = useMemo<Spaltenbeschreibung[]>(
    () => [
      // **Die Periode wird im Periodenfeld gefiltert** (R839 — Eine Periode
      // liest sich als Monat und Jahr): Bis zum 2026-09-30 verlangte der
      // Trichter dieser Spalte `202701`, während die Zelle darunter „Januar
      // 2027" zeigte. Die Kursspalten daneben bleiben Zahlen.
      { id: 'periode', kopf: t('kurse.spalte.periode'), filter: 'periodenbereich' },
      ...KURSARTEN.map((k) => ({ id: k.feld, kopf: t(k.kopf), zahl: true, filter: 'bereich' as const })),
      {
        id: 'herkunft',
        kopf: t('kurse.spalte.herkunft'),
        filter: 'auswahl',
        vorrat: Object.keys(HERKUNFT).map((h) => ({ wert: h, text: t(HERKUNFT[h]!) })),
      },
      // „Anwender", wie in jedem Protokoll (7.2 Abschnitt 42, R1271) — hier stand „Erfasst von".
      { id: 'erfasstVon', kopf: t('protokoll.spalte.anwender'), filter: 'text' },
    ],
    [],
  )

  /**
   * Was eine Zelle zum Sortieren und Filtern hergibt.
   *
   * **Die Herkunftsspalte trägt alle drei** — durch einen senkrechten Strich
   * getrennt. „Zeige mir die fortgeschriebenen" meint die Zeilen, in denen
   * *einer* fortgeschrieben ist; eine Spalte, die sich für eine der drei
   * entscheiden müsste, gäbe die falsche Auskunft.
   */
  function lies(zeile: Kurstafelzeile, spalte: string): Filterzelle {
    switch (spalte) {
      case 'periode':
        return zeile.periode
      case 'stichtag':
        return zeile.stichtag?.wert ?? null
      case 'durchschnitt':
        return zeile.durchschnitt?.wert ?? null
      case 'ytd':
        return zeile.ytd?.wert ?? null
      case 'herkunft':
        return [zeile.stichtag, zeile.durchschnitt, zeile.ytd]
          .map((z) => z?.herkunft)
          .filter((h): h is NonNullable<typeof h> => Boolean(h))
          .join('|')
      default:
        // Gefiltert wird am Namen, wie er in der Zelle steht (R838).
        return [zeile.stichtag, zeile.durchschnitt, zeile.ytd]
          .map((z) => (z ? anwendername(z.erfasstVon) : null))
          .filter(Boolean)
          .join(' ')
    }
  }

  const gezeigt = useMemo(() => {
    const roh = kurse?.zeilen ?? []
    return sortiere(gefiltert(roh, spalten, filter, lies), wahl, lies)
  }, [kurse, filter, wahl, spalten])

  /*
    **Die Jahresklammer** (R653). Vierundzwanzig Monate passen auf keinen
    Schirm; dreizehn Zeilen tun es. Gegliedert wird nur, solange nach der
    Periode sortiert ist — sonst zerschnitte die Klammer die Sortierung.
  */
  const jahre = useMemo(
    () => [...new Set(gezeigt.map((z) => Math.trunc(z.periode / 100)))].sort((a, b) => a - b),
    [gezeigt],
  )

  const gegliedert = wahl === null || wahl.spalte === 'periode'
  const juengstes = jahre.length > 0 ? jahre[jahre.length - 1] : null

  /**
   * Welche Jahre offen stehen.
   *
   * **`null` heißt: die Vorgabe gilt** — das jüngste Jahr offen, jedes ältere
   * zu. Ein gesetztes Datum wäre falsch, sobald der Anwender den Zeitraum
   * ändert; abgeleitet stimmt es auch dann noch, und ein Wechsel des
   * Währungspaars setzt es zurück.
   */
  const [offen, setOffen] = useState<Set<number> | null>(null)

  const istOffen = (jahr: number) => (offen === null ? jahr === juengstes : offen.has(jahr))

  function klappe(jahr: number) {
    const naechste = new Set(jahre.filter(istOffen))
    if (naechste.has(jahr)) {
      naechste.delete(jahr)
    } else {
      naechste.add(jahr)
    }

    setOffen(naechste)
  }

  const paare = (tafel?.paare ?? []).filter((p) => {
    const text = suche.trim().toLowerCase()
    return (
      text.length === 0 ||
      `${p.von}${p.nach}`.toLowerCase().includes(text) ||
      p.einheiten.some((e) => e.toLowerCase().includes(text))
    )
  })

  const gewaehlteZelle: Kurszelle | null =
    zelle === null || kurse === null
      ? null
      : (kurse.zeilen.find((z) => z.periode === zelle.periode)?.[
          KURSARTEN.find((k) => k.art === zelle.art)!.feld
        ] ?? null)

  const gewaehlteZeile = zelle === null ? null : (kurse?.zeilen.find((z) => z.periode === zelle.periode) ?? null)

  async function lade() {
    if (!paar) {
      return
    }

    setKurse(await api.kurstafel(paar.von, paar.nach, von ?? undefined, bis ?? undefined))
    setTafel(await api.waehrungspaare())
  }

  /**
   * Eine Arbeit ausführen und ihren Befund zeigen.
   *
   * **Der Befund des Servers steht da, nicht „ein Fehler ist aufgetreten".**
   * Ein Paar, das die EZB nicht hergibt, ist keine Störung, sondern die
   * Auskunft, wegen derer es die Prüfung gibt — mit
   * Ursache und Weg (R145). Sie kommen als **Schlüssel mit Werten** und werden
   * hier gesetzt, nicht formuliert (R163): So wechselt auch eine stehende
   * Meldung die Sprache mit.
   */
  async function mit<T>(arbeit: () => Promise<T>, dann?: (wert: T) => void, ezb = false) {
    setLaeuft(true)
    setMeldung(null)
    setBefund(null)
    if (ezb) {
      setEzbSeit(Date.now())
    }
    try {
      dann?.(await arbeit())
    } catch (fehler) {
      // Ein Befund steht hier; eine Störung — auch die schweigende EZB —
      // steht im Störungsfenster (7.2 Abschnitt 30, R1003, R1008).
      if (fehler instanceof Rechenabbruch) {
        setBefund(fehler)
      } else {
        setMeldung(fachlicherSatz(fehler))
      }
    } finally {
      setLaeuft(false)
      setEzbSeit(null)
    }
  }

  /**
   * Eine Monatszeile der Kurstabelle.
   *
   * **Eine Stelle, zwei Ansichten** (R653): Gegliedert nach Jahr steht sie
   * unter ihrer Jahresklammer, nach einer anderen Sortierung ohne sie — und
   * sie sieht in beiden Fällen gleich aus, weil es sie nur einmal gibt.
   */
  function monatszeile(zeile: Kurstafelzeile) {
    // **Ohne Tafel keine Zeile.** Der Aufruf liegt innerhalb von `kurse !==
    // null`; der frühe Ausstieg sagt es dem Übersetzer, statt es ihm mit
    // einem Ausrufezeichen zu versprechen.
    if (kurse === null) {
      return null
    }

    return (
      <tr key={zeile.periode}>
        <td>{periodenname(zeile.periode)}</td>

        {KURSARTEN.map((k) => (
          <Kursfeld
            key={k.art}
            zelle={zeile[k.feld]}
            vorschlag={k.art === 'Ytd' ? zeile.ytdVorschlag : null}
            gewaehlt={zelle?.periode === zeile.periode && zelle.art === k.art}
            laeuft={laeuft}
            aufWahl={() => setZelle({ periode: zeile.periode, art: k.art })}
            aufUnlesbar={() => setMeldung(t('tafel.zelle.keinezahl'))}
            aufWert={
              darf('kursSetzen')
                ? (wert) =>
                    mit(
                      () =>
                        api.kursSetzen({
                          von: kurse.von,
                          nach: kurse.nach,
                          periode: zeile.periode,
                          art: k.art,
                          wert,
                        }),
                      setKurse,
                    )
                : undefined
            }
          />
        ))}

        <td>
          {herkuenfte(zeile).map((h) => (
            <span key={h} className={`herkunftsmarke ${h}`}>
              {t(HERKUNFT[h] ?? 'kurse.herkunft.unbekannt')}
            </span>
          ))}
        </td>

        <td className="erlaeuterung">
          {/* Wer erfasst hat, beim Namen — „system" heißt Einrichtung (R838). */}
          {[zeile.stichtag, zeile.durchschnitt, zeile.ytd]
            .map((z) => (z ? anwendername(z.erfasstVon) : null))
            .filter((v, i, alle): v is string => Boolean(v) && alle.indexOf(v) === i)
            .join(', ') || '—'}
        </td>
      </tr>
    )
  }
  return (
    <section className="wechselkurse">
      {/*
        ---- Oben: der Befehl über alle Tabellen (R651) ------------------

        **Er steht über der Auswahl und nicht in ihrem Kopf.** Ein Sammelabruf
        grenzt den Bestand nicht ein, er kommt hinzu — dieselbe Entscheidung
        wie beim Datenimport (5.1).
      */}
      <div className="bereichseinstieg kurssammler">
        <p className="erlaeuterung">
          {tafel?.ezb ? t('kurse.sammel.hinweis') : t('kurse.quelle.ezb.zu')}
        </p>

        <div className="kurssammelfelder">
          <Periodenfeld
            wert={sammelVon}
            aufWert={setSammelVon}
            beschriftung={t('kurse.zeitraum.von')}
            klasse="feld"
            gesperrt={laeuft}
          />
          <Periodenfeld
            wert={sammelBis}
            aufWert={setSammelBis}
            beschriftung={t('kurse.zeitraum.bis')}
            klasse="feld"
            gesperrt={laeuft}
          />

          <button
            type="button"
            disabled={laeuft || !(tafel?.ezb ?? false) || sammelVon === null || sammelBis === null}
            title={
              tafel?.ezb
                ? t('kurse.sammel.hilfe', { adresse: tafel.ezbAdresse })
                : t('kurse.quelle.ezb.zu')
            }
            onClick={() => {
              if (sammelVon !== null && sammelBis !== null) {
                void mit(() => api.ezbkurseAlle(sammelVon, sammelBis), setVorschau, true)
              }
            }}
          >
            {t('kurse.sammel')}
          </button>
        </div>
      </div>

      {/* Über alle drei Spalten, unter dem Befehl, der den Abruf auslöst (R1008). */}
      {ezbSeit !== null && <Ezbwarten seit={ezbSeit} />}

      {/* ---- Links: die Währungstabellen ------------------------------- */}

      <nav className="kurspaare" aria-label={t('kurse.paare')}>
        <h3>{t('kurse.paare')}</h3>

        {/*
          **Der Knopf steht über der Suche** — wie das Anlegen in der
          Stammdatenpflege: Erst die Handlung, dann das Eingrenzen dessen, was
          schon da ist.
        */}
        {darf('kurstabelleAnlegen') && (
          <p className="handreichung">
            <button type="button" disabled={laeuft} onClick={() => setAnlegen(true)}>
              {t('kurse.tabelle.neu')}
            </button>
          </p>
        )}

        {anlegen && (
          <div className="kurstabelleneu">
            <h4>{t('kurse.tabelle.neu.titel')}</h4>

            <label className="feld">
              <span className="name">{t('kurse.tabelle.neu.von')}</span>
              <Auswahlfeld
                wert={neuVon}
                leer="—"
                beschriftung={t('kurse.tabelle.neu.von')}
                eintraege={waehrungen.map(ausStammdatum)}
                aufWahl={setNeuVon}
              />
            </label>

            <label className="feld">
              <span className="name">{t('kurse.tabelle.neu.nach')}</span>
              <Auswahlfeld
                wert={neuNach}
                leer="—"
                beschriftung={t('kurse.tabelle.neu.nach')}
                eintraege={waehrungen.map(ausStammdatum)}
                aufWahl={setNeuNach}
              />
            </label>

            <p className="erlaeuterung">{t('kurse.tabelle.neu.erlaeuterung')}</p>

            <p className="handreichung">
              <button
                type="button"
                className="haupt"
                disabled={laeuft || neuVon === '' || neuNach === ''}
                onClick={() => void legeTabelleAn()}
              >
                {t('kurse.tabelle.anlegen')}
              </button>{' '}
              <button type="button" onClick={() => setAnlegen(false)}>
                {t('import.schliessen')}
              </button>
            </p>
          </div>
        )}

        <div className="stammdatensuche">
          <input
            type="search"
            value={suche}
            placeholder={t('kurse.paare.suche')}
            aria-label={t('kurse.paare.suche')}
            onChange={(e) => setSuche(e.target.value)}
          />
        </div>

        <ul className="kurspaarliste">
          {paare.map((p) => (
            <li key={`${p.von}-${p.nach}`}>
              <button
                type="button"
                className={`kurspaarzeile${
                  paar?.von === p.von && paar?.nach === p.nach ? ' aktiv' : ''
                }${p.gepflegt ? '' : ' vorgeschlagen'}`}
                onClick={() => setPaar({ von: p.von, nach: p.nach })}
              >
                <span className="paar">
                  {p.von} <span aria-hidden="true">→</span> {p.nach}
                </span>
                <Paarzusatz paar={p} />
              </button>
            </li>
          ))}
        </ul>

        {paare.length === 0 && <p className="hinweis">{t('kurse.paare.keine')}</p>}
      </nav>

      {/* ---- Mitte: die Kurstabelle ------------------------------------ */}

      <div className="kurstafel">
        {meldung && <p className="hinweis">{meldung}</p>}

        {befund && (
          <div className="meldung">
            <p>{satz(befund.meldung)}</p>
            {befund.handreichung && <p className="handreichung">{satz(befund.handreichung)}</p>}
          </div>
        )}

        {paar === null && (
          <div className="meldung neutral">
            <h2>{t('kurse.leer.titel')}</h2>
            <p>{t('kurse.leer.text')}</p>
          </div>
        )}

        {paar !== null && (
          <>
            <div className="berichtsleiste kursleiste">
              {/* MM.JJJJ wie jede Periodeneingabe in der Anwendung (R502). */}
              <Periodenfeld
                wert={von}
                aufWert={setVon}
                beschriftung={t('kurse.zeitraum.von')}
                klasse="feld"
              />
              <Periodenfeld
                wert={bis}
                aufWert={setBis}
                beschriftung={t('kurse.zeitraum.bis')}
                klasse="feld"
              />
              <Quellen
                paar={paar}
                vonPeriode={von ?? tafel?.geschaeftsjahr.von ?? 0}
                bisPeriode={bis ?? tafel?.geschaeftsjahr.bis ?? 0}
                tafel={kurse}
                ezbOffen={tafel?.ezb ?? false}
                ezbAdresse={tafel?.ezbAdresse ?? ''}
                laeuft={laeuft}
                aufVorschau={setVorschau}
                aufArbeit={mit}
              />
            </div>

            {kurse === null && <p className="hinweis">{t('vorgang.laedt')}</p>}

            {kurse !== null && (
              <>
                <p className="erlaeuterung">
                  {t('kurse.tafel.erlaeuterung', {
                    von: kurse.von,
                    nach: kurse.nach,
                    beginn: String(kurse.geschaeftsjahrBeginn),
                  })}
                  {anzahlFilter(filter) > 0 && (
                    <>
                      {' '}
                      {t('filter.wirkt', {
                        spalten: anzahlwort(anzahlFilter(filter)),
                        gezeigt: String(gezeigt.length),
                        gesamt: anzahlwort(kurse.zeilen.length),
                      })}{' '}
                      <button type="button" className="schmal" onClick={() => setFilter({})}>
                        {t('filter.alleloesen')}
                      </button>
                    </>
                  )}
                </p>

                <div className="tabellenrahmen kurstabelle">
                  <table ref={spaltenrahmen} className="ziehbaretabelle">
                    <Spaltenmassleiste masse={spaltenmasse} />
                    <thead>
                      <Filterkopfzeile
                        spalten={spalten}
                        wahl={wahl}
                        aufWahl={setWahl}
                        stand={filter}
                        aufStand={setFilter}
                      />
                    </thead>
                    <tbody>
                      {/*
                        **Gegliedert nach Jahr, solange nach der Periode
                        sortiert ist** (R653). Wer nach dem Stichtagskurs
                        sortiert, will die Reihenfolge der Kurse sehen; eine
                        Jahresklammer darüber zerschnitte sie und sähe dabei
                        aus, als sortiere sie nicht.
                      */}
                      {gegliedert
                        ? jahre.map((jahr) => (
                            <Fragment key={jahr}>
                              <tr className="jahreszeile">
                                <th colSpan={spalten.length} scope="rowgroup">
                                  <button
                                    type="button"
                                    className="jahresklammer"
                                    aria-expanded={istOffen(jahr)}
                                    onClick={() => klappe(jahr)}
                                  >
                                    <span className="pfeil" aria-hidden="true">
                                      {istOffen(jahr) ? '▾' : '▸'}
                                    </span>
                                    {jahr}
                                  </button>
                                </th>
                              </tr>

                              {istOffen(jahr) &&
                                gezeigt
                                  .filter((z) => Math.trunc(z.periode / 100) === jahr)
                                  .map(monatszeile)}
                            </Fragment>
                          ))
                        : gezeigt.map(monatszeile)}
                    </tbody>
                  </table>
                </div>

                {gezeigt.length === 0 && <p className="hinweis">{t('kurse.tafel.leer')}</p>}
              </>
            )}
          </>
        )}
      </div>

      {/* ---- Rechts: die Herkunft der Zelle ----------------------------- */}

      <aside className="kursnachweis">
        <Zellennachweis
          zelle={gewaehlteZelle}
          wahl={zelle}
          zeile={gewaehlteZeile}
          laeuft={laeuft}
          aufZuruecknehmen={
            darf('kursZuruecknehmen')
              ? () =>
                  kurse &&
                  zelle &&
                  mit(
                    () =>
                      api.kursZuruecknehmen({
                        von: kurse.von,
                        nach: kurse.nach,
                        periode: zelle.periode,
                        art: zelle.art,
                      }),
                    (neu) => {
                      setKurse(neu)
                      setZelle(null)
                    },
                  )
              : undefined
          }
        />

      </aside>

      {vorschau && (
        <Vorschaufenster
          vorschau={vorschau}
          laeuft={laeuft}
          aufSchliessen={() => setVorschau(null)}
          aufUebernehmen={
            darf('kurseUebernehmen')
              ? () =>
                  mit(
                    () => api.kurseUebernehmen(vorschau),
                    () => {
                      setVorschau(null)
                      void lade()
                    },
                  )
              : undefined
          }
        />
      )}
    </section>
  )
}

/**
 * Die Herkünfte einer Zeile, jede einmal.
 *
 * **Bis zu drei Kurse, bis zu drei Herkünfte** — und sie stehen alle da: Eine
 * Zeile, in der der Stichtagskurs gemeldet und der Durchschnittskurs
 * fortgeschrieben ist, sagt etwas anderes als eine, in der beides gemeldet
 * ist (R302).
 */
function herkuenfte(zeile: Kurstafelzeile): string[] {
  const alle = [zeile.stichtag, zeile.durchschnitt, zeile.ytd]
    .map((z) => z?.herkunft)
    .filter((h): h is NonNullable<typeof h> => Boolean(h))

  return [...new Set<string>(alle)]
}

/**
 * Was unter dem Währungspaar steht.
 *
 * **Eine Lücke ist die wichtigere Zahl als der Bestand.** Dass eine Tabelle
 * 48 Zeilen hat, ist eine Mengenangabe; dass drei Monate keinen
 * Stichtagskurs haben, ist die Auskunft, wegen der man hinsieht — jeder davon
 * blockiert die Umrechnung (R42).
 */
function Paarzusatz({ paar }: { paar: Waehrungspaar }) {
  /*
    **Wo die EZB nichts hergibt, steht es am Paar** (R684) — bevor jemand den
    Knopf drückt und nicht in der Meldung danach. Sie führt neunundzwanzig
    Währungen, und nur gegen den Euro; für den kenianischen Schilling holt der
    Sammelabruf nichts.
  */
  const ohneEzb = !paar.ezbMoeglich && (
    paar.von === 'EUR' || paar.nach === 'EUR' ? (
      <span className="zusatz ohneezb">
        {t('kurse.paar.ohneezb', { waehrung: paar.von === 'EUR' ? paar.nach : paar.von })}
      </span>
    ) : (
      <span className="zusatz ohneezb">{t('kurse.paar.ohneeuro')}</span>
    )
  )

  if (paar.herkunft === 'angelegt') {
    return (
      <>
        <span className="zusatz angelegt">{t('kurse.paar.angelegt')}</span>
        {ohneEzb}
      </>
    )
  }

  if (!paar.gepflegt) {
    return (
      <>
        <span className="zusatz vorgeschlagen">
          {t('kurse.paar.vorgeschlagen', { einheiten: paar.einheiten.join(', ') || '—' })}
        </span>
        {ohneEzb}
      </>
    )
  }

  return (
    <>
      <span className="zusatz">
        {t('kurse.paar.bestand', {
          zeilen: anzahlwort(paar.zeilen),
          von: paar.vonPeriode ? periodenname(paar.vonPeriode) : '—',
          bis: paar.bisPeriode ? periodenname(paar.bisPeriode) : '—',
        })}
        {paar.luecken > 0 && (
          <span className="luecke">{t('kurse.paar.luecken', { anzahl: anzahlwort(paar.luecken) })}</span>
        )}
      </span>
      {ohneEzb}
    </>
  )
}

/**
 * Eine Kurszelle — anzeigen und ändern an derselben Stelle.
 *
 * **Geändert wird in der Tabelle und nicht in einem Formular daneben.** Wer
 * zwölf Monatskurse nachträgt, tippt sie untereinander; ein Formular, das
 * jedes Mal aufgeht und zugeht, wäre zwölfmal derselbe Umweg. Die Zelle ist
 * ein Knopf, bis man sie anklickt, und danach ein Feld — dieselbe Stelle,
 * dieselbe Breite, kein Sprung.
 *
 * **Die Sperre je Monat ist am 2026-09-21 zurückgebaut worden** und wartet auf
 * den Audit-Trail (ROADMAP Bereich 8): Ein Sperrbegriff, der an einem Bereich
 * entsteht, wird dort zur Gewohnheit — und die nächste Sperre bekäme ihre
 * eigene Bauform.
 */
function Kursfeld({
  zelle,
  vorschlag,
  gewaehlt,
  laeuft,
  aufWahl,
  aufWert,
  aufUnlesbar,
}: {
  zelle: Kurszelle | null
  /** Was CoKo rechnen würde — nur an der YTD-Spalte (R647). */
  vorschlag: number | null
  gewaehlt: boolean
  laeuft: boolean
  aufWahl: () => void
  /** Fehlt ohne Recht — dann öffnet der Klick nur den Nachweis, kein Feld (R1180). */
  aufWert?: (wert: number) => void
  /** Das Feld wurde mit etwas verlassen, das sich nicht als Kurs liest. */
  aufUnlesbar: () => void
}) {
  const [eingabe, setEingabe] = useState<string | null>(null)
  const feld = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (eingabe !== null) {
      feld.current?.select()
    }
  }, [eingabe])

  /*
    **Gelesen wie jede Zahl der Stammdaten** (R840 — Eine Zahl in den
    Stammdaten steht wie im Bericht): nach dem Zahlenformat der Fußleiste,
    mit oder ohne Tausenderpunkt. Bis zum 2026-09-30 wurde nur das erste
    Komma zum Punkt — „1.234,5" beim Yen ergab keine Zahl, und das Feld
    schloss still, ohne zu sagen, warum nichts geschrieben wurde.
  */
  function fertig() {
    const text = (eingabe ?? '').trim()
    setEingabe(null)

    if (text === '') {
      return
    }

    const gelesen = liesDezimal(text)
    const wert = gelesen === null ? Number.NaN : Number(gelesen)

    // Ein Kurs ist größer als null — null oder weniger ist keiner (R145).
    if (!Number.isFinite(wert) || wert <= 0) {
      aufUnlesbar()
      return
    }

    if (wert !== zelle?.wert) {
      aufWert?.(wert)
    }
  }

  if (eingabe !== null) {
    return (
      <td className="zahl kursfeld">
        <input
          ref={feld}
          type="text"
          inputMode="decimal"
          value={eingabe}
          disabled={laeuft}
          onChange={(e) => setEingabe(e.target.value)}
          onBlur={fertig}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              fertig()
            }

            if (e.key === 'Escape') {
              setEingabe(null)
            }
          }}
        />
      </td>
    )
  }

  return (
    <td aria-current={gewaehlt ? true : undefined} className={`zahl kursfeld${gewaehlt ? ' gewaehlt' : ''}`}>
      <button
        type="button"
        className={`kurswert${zelle ? '' : ' leer'}`}
        title={aufWert ? t('kurse.zelle.hilfe') : undefined}
        onClick={() => {
          aufWahl()
          // Im Feld steht der Kurs wie in der Zelle — „1,0856", nicht
          // „1.0856" (R840).
          if (aufWert) {
            setEingabe(zelle ? alsEingabe(String(zelle.wert), 'kurs') : '')
          }
        }}
      >
        {zelle ? kurswert(zelle.wert) : '—'}
      </button>

      {/*
        **Der gerechnete Vorschlag steht auch dann da, wenn schon ein Kurs
        gepflegt ist** — solange er abweicht. Sonst sähe der Anwender nie, dass
        sein eingetragener YTD-Kurs von dem abweicht, was aus den Monaten
        folgt, und genau das ist die Frage, die der Prüfer stellt.
      */}
      {vorschlag !== null && (zelle === null || zelle.wert !== vorschlag) && (
        <span className="vorschlagswert" title={t('kurse.ytd.vorschlag.hilfe')}>
          {kurswert(vorschlag)}
        </span>
      )}
    </td>
  )
}

/** Die drei Quellen als drei Knöpfe — und eine Vorschau (R647–R649). */
function Quellen({
  paar,
  vonPeriode,
  bisPeriode,
  tafel,
  ezbOffen,
  ezbAdresse,
  laeuft,
  aufVorschau,
  aufArbeit,
}: {
  paar: { von: string; nach: string }
  /**
   * Der Zeitraum **aus den Feldern der Leiste** — und wo keine getippt sind,
   * das laufende Geschäftsjahr (R652).
   *
   * **Nicht aus der geladenen Tafel.** Bis zum 2026-09-21 stand hier ihr
   * Fenster, und das ist ein Kreis: Ein neues Paar hat keine Tafel, die Tafel
   * kein Fenster, das Fenster keinen Zeitraum — der Aufruf fragte im Regelfall
   * nach nichts, also genau dann, wenn es noch keine Kurse gibt.
   */
  vonPeriode: number
  bisPeriode: number
  tafel: Kurstafel | null
  ezbOffen: boolean
  ezbAdresse: string
  laeuft: boolean
  aufVorschau: (vorschau: Kursvorschau) => void
  aufArbeit: <T>(arbeit: () => Promise<T>, dann?: (wert: T) => void, ezb?: boolean) => Promise<void>
}) {
  const darf = useDarf()

  return (
    <div className="kursquellen">
      {/*
        **Die Dateiwahl der Anwendung** (R949) statt eines Knopfes, der ein
        verstecktes Feld anklickt: Der wirkte nur im Browser (R451). Ohne
        Recht ist sie nicht da (R1180).
      */}
      {darf('kursdatei') && (
        <Dateiwahl
          beschriftung={t('kurse.quelle.datei')}
          accept=".xlsx,.csv,.txt"
          gesperrt={laeuft}
          ohneNamen
          leeren
          aufWahl={(dateien) => {
            const gewaehlt = dateien[0]
            if (gewaehlt) {
              void aufArbeit(() => api.kursdatei(gewaehlt), aufVorschau)
            }
          }}
        />
      )}

      {/*
        **Der Knopf ist da und sagt, warum er nicht geht** — anders als in der
        Leiste, wo Fehlendes weggelassen wird (R155). Der Unterschied: Ein
        fehlendes Recht ist keine Aufgabe, ein gesperrter Netzweg schon. Wer
        ihn öffnen lassen will, muss wissen, dass es ihn gibt.
      */}
      <button
        type="button"
        disabled={laeuft || !ezbOffen || vonPeriode === 0 || bisPeriode === 0}
        title={ezbOffen ? t('kurse.quelle.ezb.hilfe', { adresse: ezbAdresse }) : t('kurse.quelle.ezb.zu')}
        onClick={() =>
          void aufArbeit(() => api.ezbkurse(paar.von, paar.nach, vonPeriode, bisPeriode), aufVorschau, true)
        }
      >
        {t('kurse.quelle.ezb')}
      </button>

      <button
        type="button"
        disabled={laeuft || tafel === null}
        title={t('kurse.quelle.ytd.hilfe')}
        onClick={() =>
          void aufArbeit(() => api.ytdkurse(paar.von, paar.nach, vonPeriode, bisPeriode), aufVorschau)
        }
      >
        {t('kurse.quelle.ytd')}
      </button>
    </div>
  )
}

/**
 * Die Herkunft der angeklickten Zelle.
 *
 * **Sie steht rechts und nicht als Fußnote an der Zahl** — dieselbe
 * Entscheidung wie im Eigenschaftsblatt (7.4, Abschnitt 6): Eine Zelle mit
 * vier Zusätzen ist unlesbar, eine Spalte mit vier Zeilen ist eine Liste.
 */
function Zellennachweis({
  zelle,
  wahl,
  zeile,
  laeuft,
  aufZuruecknehmen,
}: {
  zelle: Kurszelle | null
  wahl: { periode: number; art: Kursart } | null
  zeile: Kurstafelzeile | null
  laeuft: boolean
  /** Fehlt ohne Recht — dann stehen Knopf und Hinweis nicht da (R1180). */
  aufZuruecknehmen?: () => void
}) {
  if (wahl === null) {
    return (
      <div className="kursblock">
        <h3>{t('kurse.nachweis')}</h3>
        <p className="erlaeuterung">{t('kurse.nachweis.leer')}</p>
      </div>
    )
  }

  const artname = t(KURSARTEN.find((k) => k.art === wahl.art)!.kopf)

  return (
    <div className="kursblock">
      <h3>{t('kurse.nachweis')}</h3>
      <p className="kursnachweiskopf">
        {periodenname(wahl.periode)} · {artname}
      </p>

      {zelle === null ? (
        <>
          <p className="erlaeuterung">{t('kurse.nachweis.keinkurs')}</p>
          {wahl.art === 'Ytd' && zeile?.ytdVorschlag !== null && zeile !== null && (
            <p className="erlaeuterung">
              {t('kurse.nachweis.ytdvorschlag', { wert: kurswert(zeile.ytdVorschlag!) })}
            </p>
          )}
        </>
      ) : (
        <dl className="kursdaten">
          <dt>{t('kurse.nachweis.wert')}</dt>
          <dd className="zahl">{kurswert(zelle.wert)}</dd>

          <dt>{t('kurse.spalte.herkunft')}</dt>
          <dd>
            {zelle.herkunft ? (
              <span className={`herkunftsmarke ${zelle.herkunft}`}>
                {t(HERKUNFT[zelle.herkunft] ?? 'kurse.herkunft.unbekannt')}
              </span>
            ) : (
              t('kurse.herkunft.unbekannt')
            )}
          </dd>

          {zelle.nachweis && (
            <>
              <dt>{t('kurse.nachweis.woher')}</dt>
              <dd className="erlaeuterung">{ohneRegelvermerk(zelle.nachweis)}</dd>
            </>
          )}

          <dt>{t('kurse.nachweis.erfasst')}</dt>
          <dd className="erlaeuterung">
            {/* Mit Uhrzeit: Kurse werden am Abschlusstag nachgezogen (R979). */}
            {zeitpunkt(zelle.erfasstAm)} · {anwendername(zelle.erfasstVon)}
          </dd>
          {/* Ohne „4 Fassungen" (7.2 Abschnitt 36, R1141, R1143): Der Stand heißt
              nach seinem Zeitpunkt, und der steht eine Zeile darüber. */}
        </dl>
      )}

      {zelle !== null && aufZuruecknehmen && (
        <p className="handreichung">
          <button type="button" disabled={laeuft} onClick={aufZuruecknehmen}>
            {t('kurse.zuruecknehmen')}
          </button>
        </p>
      )}
      {zelle !== null && aufZuruecknehmen && <p className="erlaeuterung">{t('kurse.zuruecknehmen.hinweis')}</p>}
    </div>
  )
}

/**
 * Was eine Lieferung bewirken würde — bevor etwas geschrieben wird.
 *
 * **Dieselbe Bauform wie beim Datenimport** (5.1, R132): sehen, was daraus
 * würde, dann übernehmen — und übernommen wird genau das, was auf dem Schirm
 * stand, nicht das Ergebnis einer zweiten Lesung.
 */
function Vorschaufenster({
  vorschau,
  laeuft,
  aufSchliessen,
  aufUebernehmen,
}: {
  vorschau: Kursvorschau
  laeuft: boolean
  aufSchliessen: () => void
  /** Fehlt ohne Recht — die Vorschau bleibt lesbar, ohne Übernehmen (R1180). */
  aufUebernehmen?: () => void
}) {
  /*
    **Die Spaltenbreiten zieht der Anwender** (7.3 Abschnitt 10, R415 —
    Auftrag vom 2026-09-29). Hier je Spalte eine Breite: Die Spalten dieser
    Tabelle tragen verschiedene Dinge und haben nichts gleichzumachen.
  */
  const { masse: spaltenmasse, rahmen: spaltenrahmen } =
    useSpaltenmasse<HTMLTableElement>('wechselkurse:vorschaufenster')

  const schreibt = vorschau.neu + vorschau.geaendert

  /*
    **Das übliche Fenster, bildschirmfüllend** (7.2 Abschnitt 41, R1260):
    verschieben am Kopf, groß und klein im Kopf, die Ecke zieht, Esc und Kreuz
    schließen. Titel und Knöpfe bleiben beim Rollen stehen (R915, Auftrag vom
    2026-10-03) — Kopf und Fuß des Fensters stehen außerhalb des rollenden
    Teils.

    **Breit, weil es eine Tabelle mit sechs Spalten ist** und der Befund
    seinen Grund als Satz darunter trägt. Das alte Fenster war 1000 Punkte
    breit; in 760 bräche jeder Grund mehrfach um. Und eine Lieferung der EZB
    bringt jede Währung mit Stichtags- und Durchschnittskurs — eine lange
    Liste, die die ganze Fläche brauchen kann.

    **Solange übernommen wird, schließen Esc und Kreuz nicht** — der Knopf
    *Abbrechen* ist dann ebenso gesperrt.
  */
  return (
    <Dialogfenster
      titel={t(`kurse.vorschau.${vorschau.quelle}` as Sprachschluessel)}
      kopfzusatz={vorschau.bezeichnung}
      weite="breit"
      aufSchliessen={() => {
        if (!laeuft) {
          aufSchliessen()
        }
      }}
      fuss={
        <>
          {aufUebernehmen && (
            <button type="button" className="haupt" disabled={laeuft || schreibt === 0} onClick={aufUebernehmen}>
              {t('kurse.vorschau.uebernehmen', { anzahl: anzahlwort(schreibt) })}
            </button>
          )}
          <button type="button" disabled={laeuft} onClick={aufSchliessen}>
            {t('kurse.vorschau.abbrechen')}
          </button>
        </>
      }
    >
      <div className="kursvorschau">

        <div className="kennzahlen">
          <Kennzahl name={t('kurse.befund.neu')}>{vorschau.neu}</Kennzahl>
          <Kennzahl name={t('kurse.befund.geaendert')}>{vorschau.geaendert}</Kennzahl>
          <Kennzahl name={t('kurse.befund.unveraendert')}>{vorschau.unveraendert}</Kennzahl>
          <Kennzahl name={t('kurse.befund.abgewiesen')}>{vorschau.abgewiesen}</Kennzahl>
        </div>

        {vorschau.zeilen.length === 0 && <p className="hinweis">{t('kurse.vorschau.leer')}</p>}

        {vorschau.zeilen.length > 0 && (
          <div className="tabellenrahmen">
            <table ref={spaltenrahmen} className="ziehbaretabelle">
              <Spaltenmassleiste masse={spaltenmasse} />
              <thead>
                <tr>
                  <th>{t('kurse.spalte.periode')}</th>
                  <th>{t('kurse.spalte.paar')}</th>
                  <th>{t('kurse.spalte.art')}</th>
                  <th className="zahl">{t('kurse.spalte.bisher')}</th>
                  <th className="zahl">{t('kurse.spalte.neu')}</th>
                  <th>{t('kurse.spalte.befund')}</th>
                </tr>
              </thead>
              <tbody>
                {vorschau.zeilen.map((z, i) => (
                  <tr key={`${z.periode}-${z.art}-${i}`}>
                    <td>{z.periode > 0 ? periodenname(z.periode) : '—'}</td>
                    <td>
                      {z.von} <span aria-hidden="true">→</span> {z.nach}
                    </td>
                    <td>{t(`kurse.art.${z.art.toLowerCase()}` as Sprachschluessel)}</td>
                    <td className="zahl">{z.bisher === null ? '—' : kurswert(z.bisher)}</td>
                    {/*
                      **Eine abgewiesene Zeile trägt keinen Kurs.** Ein Paar
                      ohne Euro kommt mit dem Wert 0 aus dem Sammellauf — ihn
                      als „0,0000000000" zu setzen, hieße eine Zahl zu zeigen,
                      wo keine gemeldet wurde (R651).
                    */}
                    <td className="zahl">{z.befund === 'abgewiesen' ? '—' : kurswert(z.wert)}</td>
                    <td>
                      <span className={`befundmarke ${z.befund}`}>
                        {t(BEFUND[z.befund] ?? 'kurse.befund.abgewiesen')}
                      </span>
                      {/* Sätze des Rechenkerns, ohne Regelnummer gelesen (7.2 Abschnitt 34, R1133). */}
                      {z.fehler && <div className="erlaeuterung">{ohneRegelvermerk(z.fehler)}</div>}
                      {!z.fehler && z.nachweis && <div className="erlaeuterung">{ohneRegelvermerk(z.nachweis)}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialogfenster>
  )
}

function Kennzahl({ name, children }: { name: string; children: React.ReactNode }) {
  return (
    <div className="kennzahl">
      <span className="name">{name}</span>
      <span className="wert">{children}</span>
    </div>
  )
}

/**
 * **Während die EZB gefragt wird, steht da, was geschieht** (7.2 Abschnitt
 * 30, R1008) — die Auflage des Auftraggebers: *„Dabei bitte mit dem Anwender
 * immer kommunizieren und einen Fortschrittsbalken oder Hinweistext zeigen,
 * dass im Falle der EZB erneut versucht wird und nach 30 Sekunden abgebrochen
 * wird."*
 *
 * Gewöhnlich antwortet die EZB in einer Drittelsekunde. Dauert es länger als
 * drei, ist sie langsam oder schweigt, und der Satz sagt, daß weiter gefragt
 * wird. Der allgemeine Wartehinweis am unteren Rand tritt solange zurück — ein
 * Warten, zweimal gesagt, ist einmal zuviel.
 */
function Ezbwarten({ seit }: { seit: number }) {
  const [jetzt, setJetzt] = useState(() => Date.now())

  useEffect(() => {
    const zurueck = zeigtSelbst()
    const uhr = window.setInterval(() => setJetzt(Date.now()), 250)
    return () => {
      window.clearInterval(uhr)
      zurueck()
    }
  }, [])

  const sekunden = Math.min(30, Math.floor((jetzt - seit) / 1000))

  return (
    <div className="ezbwarten" role="status">
      <p>
        {jetzt - seit < 3000
          ? t('kurse.ezb.warten')
          : t('kurse.ezb.warten.erneut', { sekunden: String(sekunden) })}
      </p>
      <progress max={30} value={sekunden} />
    </div>
  )
}
